import { useState } from 'react';
import { Star, ExternalLink, BookOpen, Wrench, Globe, X, ArrowRight, Calendar } from 'lucide-react';
import { Badge } from '../ui/badge';

export function RecommendationsSection({ recommendations, loading, emptyMessage, categories = ['book', 'tool', 'resource'] }) {
    const [selectedRecco, setSelectedRecco] = useState(null);
    
    // Filter out expired recommendations (double check client-side)
    const now = new Date().toISOString();
    const displayRecommendations = recommendations.filter(
        r => !r.expires_at || r.expires_at > now
    );

    // Group recommendations by category
    const groupedByCategory = displayRecommendations.reduce((acc, rec) => {
        const cat = rec.category || 'other';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(rec);
        return acc;
    }, {});

    // Get unique categories that have recommendations
    const activeCategories = Object.keys(groupedByCategory);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getCategoryIcon = (category) => {
        switch (category) {
            case 'book':
                return <BookOpen className="h-4 w-4" />;
            case 'tool':
                return <Wrench className="h-4 w-4" />;
            case 'resource':
                return <Globe className="h-4 w-4" />;
            default:
                return <Star className="h-4 w-4" />;
        }
    };

    const getCategoryColor = (category) => {
        switch (category) {
            case 'book':
                return 'border-blue-500 bg-blue-500/10';
            case 'tool':
                return 'border-green-500 bg-green-500/10';
            case 'resource':
                return 'border-purple-500 bg-purple-500/10';
            default:
                return 'border-primary bg-primary/10';
        }
    };

    const openReccoDetail = (rec) => {
        setSelectedRecco(rec);
    };

    const closeModal = () => {
        setSelectedRecco(null);
    };

    return (
        <div 
            className="retro-card p-6 h-full flex flex-col"
            data-testid="recommendations-section"
        >
            {/* Section Header */}
            <div className="mb-6 flex-shrink-0">
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-6 h-6 bg-primary flex items-center justify-center">
                        <Star className="h-3 w-3 text-primary-foreground" />
                    </div>
                    <h2 
                        className="text-xl sm:text-2xl tracking-tight uppercase"
                        style={{ fontFamily: "'Silkscreen', cursive" }}
                        data-testid="recommendations-title"
                    >
                        Reccos
                    </h2>
                </div>
                <p className="text-muted-foreground text-sm">
                    Books, tools, and resources I recommend.
                </p>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto max-h-[400px] pr-2 scrollbar-thin">
                {loading ? (
                    <div className="space-y-4">
                        {Array(2).fill(0).map((_, i) => (
                            <div key={i} className="p-4 border-2 border-dashed border-border">
                                <div className="h-4 w-16 bg-muted skeleton-pulse mb-2" />
                                <div className="h-5 w-3/4 bg-muted skeleton-pulse mb-2" />
                                <div className="h-4 w-full bg-muted skeleton-pulse" />
                            </div>
                        ))}
                    </div>
                ) : displayRecommendations.length > 0 ? (
                    <div className="space-y-6">
                        {/* Separate tile for each category */}
                        {activeCategories.map(category => (
                            <div 
                                key={category} 
                                className={`p-4 border-2 ${getCategoryColor(category)}`}
                                data-testid={`recco-category-${category}`}
                            >
                                {/* Category Header */}
                                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border/50">
                                    {getCategoryIcon(category)}
                                    <span 
                                        className="text-sm uppercase tracking-widest font-bold"
                                        style={{ fontFamily: "'Silkscreen', cursive" }}
                                    >
                                        {category.charAt(0).toUpperCase() + category.slice(1)}s
                                    </span>
                                    <Badge variant="outline" className="ml-auto rounded-none text-[10px]">
                                        {groupedByCategory[category].length}
                                    </Badge>
                                </div>
                                
                                {/* Items in this category */}
                                <div className="space-y-3">
                                    {groupedByCategory[category].map((rec) => (
                                        <div
                                            key={rec.id}
                                            onClick={() => openReccoDetail(rec)}
                                            className="flex gap-3 p-3 bg-background/50 border border-border/50 hover:border-primary transition-all group cursor-pointer"
                                            data-testid={`recommendation-item-${rec.id}`}
                                        >
                                            {/* Thumbnail image */}
                                            {rec.image_url && (
                                                <div className="w-16 h-16 flex-shrink-0 border-2 border-border overflow-hidden">
                                                    <img 
                                                        src={rec.image_url} 
                                                        alt={rec.title}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <h3 
                                                    className="text-sm tracking-tight group-hover:text-primary transition-colors flex items-start gap-2"
                                                    style={{ fontFamily: "'Silkscreen', cursive" }}
                                                >
                                                    {rec.title}
                                                    <ArrowRight className="h-3 w-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5" />
                                                </h3>
                                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                                    {rec.description}
                                                </p>
                                                {/* Published Date */}
                                                <div className="flex items-center gap-1 mt-1 text-[10px] text-muted-foreground uppercase tracking-widest">
                                                    <Calendar className="h-3 w-3" />
                                                    {formatDate(rec.created_at)}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="p-8 border-2 border-dashed border-border text-center">
                        <Star className="h-10 w-10 text-muted-foreground/30 mx-auto mb-4" />
                        <p 
                            className="text-muted-foreground italic text-sm"
                            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                            data-testid="recommendations-empty-message"
                        >
                            {emptyMessage}
                        </p>
                    </div>
                )}
            </div>

            {/* Recommendation Detail Modal */}
            {selectedRecco && (
                <div 
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
                    onClick={closeModal}
                >
                    <div 
                        className={`retro-card w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 relative border-2 ${getCategoryColor(selectedRecco.category)}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close button */}
                        <button 
                            onClick={closeModal}
                            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center hover:bg-muted transition-colors z-10"
                            data-testid="close-recco-modal"
                        >
                            <X className="h-5 w-5" />
                        </button>

                        {/* Category Badge */}
                        <div className="flex items-center gap-2 mb-4">
                            {getCategoryIcon(selectedRecco.category)}
                            <span 
                                className="text-xs uppercase tracking-widest"
                                style={{ fontFamily: "'Silkscreen', cursive" }}
                            >
                                {selectedRecco.category}
                            </span>
                        </div>

                        {/* Image */}
                        {selectedRecco.image_url && (
                            <div className="mb-4 border-2 border-border overflow-hidden">
                                <img 
                                    src={selectedRecco.image_url} 
                                    alt={selectedRecco.title}
                                    className="w-full h-48 object-cover"
                                />
                            </div>
                        )}

                        {/* Title */}
                        <h2 
                            className="text-xl sm:text-2xl tracking-tight uppercase mb-2 pr-8"
                            style={{ fontFamily: "'Silkscreen', cursive" }}
                        >
                            {selectedRecco.title}
                        </h2>

                        {/* Published Date */}
                        <div className="flex items-center gap-1 mb-4 text-xs text-muted-foreground uppercase tracking-widest">
                            <Calendar className="h-3 w-3" />
                            {formatDate(selectedRecco.created_at)}
                        </div>

                        {/* Description */}
                        <div className="mb-6">
                            <p 
                                className="text-sm leading-relaxed text-foreground"
                                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                            >
                                {selectedRecco.description}
                            </p>
                        </div>

                        {/* External Link */}
                        {selectedRecco.link && (
                            <a
                                href={selectedRecco.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-xs uppercase tracking-widest border-2 border-primary"
                                style={{ fontFamily: "'Silkscreen', cursive" }}
                                data-testid="recco-external-link"
                            >
                                Check it out
                                <ExternalLink className="h-4 w-4" />
                            </a>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
